import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Box,
    TextField,
    Button,
    MenuItem,
    Select,
    Grid,
    List,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Card,
    CardHeader,
    Checkbox,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ArrowForward,
    ArrowBack,
    ArrowDownward,
    ArrowUpward,
} from '@mui/icons-material';
import api from 'state/axios';
import StyledButton from 'components/StyledButton';


function not(a, b) {
    return a.filter((value) => !b.includes(value));
}

function intersection(a, b) {
    return a.filter((value) => b.includes(value));
}

function union(a, b) {
    return [...a, ...not(b, a)];
}


const EnrollmentDialog = ({ open, onClose, onSubmit, trainingCourseId }) => {
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');
    const [leftRank, setLeftRank] = useState('');
    const [rightRank, setRightRank] = useState('');
    const [leftBase, setLeftBase] = useState('');
    const [rightBase, setRightBase] = useState('');

    const [enrollmentData, setEnrollmentData] = useState({});


    const fetchData = useCallback(async (url, setter, errorMsg) => {
        try {
            const { data } = await api.get(url);
            setter(data);
            return data;
        } catch (error) {
            toast.error(error.response?.data?.message || errorMsg);
        }
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [employeesResponse, courseResponse] = await Promise.all([
                    fetchData('/employees/active', () => {}, 'Failed to fetch employees!'),
                    trainingCourseId ? fetchData(`/trainingcourses/${trainingCourseId}`, () => {}, 'Failed to fetch course!') : Promise.resolve(null),
                ]);

                setChecked([]);
                setLeftSearch('');
                setRightSearch('');
                setLeftRank('');
                setRightRank('');
                setLeftBase('');
                setRightBase('');

                const rightCrewList = (courseResponse?.enrollments?.map(m => ({
                    employeeCode: m.employeeCode,
                    employeeName: m.employeeName,
                    rank: m.rank,
                    base: m.base,
                })) || []).sort((a, b) => parseInt(a.employeeCode.match(/\d+$/)?.[0] || '0', 10) - parseInt(b.employeeCode.match(/\d+$/)?.[0] || '0', 10));
                setRight(rightCrewList);

                setEnrollmentData(
                    rightCrewList.reduce((acc, cur) => { acc[cur.employeeCode] = cur; return acc; }, {})
                );

                const enrolledCodes = new Set(rightCrewList.map(m => m.employeeCode));
                const leftCrewList = ((employeesResponse || []).filter(m => !enrolledCodes.has(m.employeeCode))
                    .map(m => ({
                        employeeCode: m.employeeCode,
                        employeeName: m.employeeName || 'Unknown',
                        rank: m.rank,
                        base: m.homeBase,
                    }))).sort((a, b) => parseInt(a.employeeCode.match(/\d+$/)?.[0] || '0', 10) - parseInt(b.employeeCode.match(/\d+$/)?.[0] || '0', 10));
                setLeft(leftCrewList);
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to fetch data!');
            }
        };

        if (open) fetchAllData();
    }, [open, fetchData, trainingCourseId]);


    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        setRight(right.concat(leftChecked));
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft(left.concat(rightChecked));
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const handleSubmit = async () => {
        if (right.length === 0) {
            toast.error('Please select at least one employee.');
            return;
        }
        try {
            const payload = right.map(m => ({
                employeeCode: m.employeeCode,
                employeeName: enrollmentData[m.employeeCode]?.employeeName || m.employeeName || '',
                rank: enrollmentData[m.employeeCode]?.rank || m.rank || '',
                base: enrollmentData[m.employeeCode]?.base || m.base || '',
                enrolledAs: enrollmentData[m.employeeCode]?.enrolledAs || 'trainee',
            }));
            await onSubmit(trainingCourseId, payload);
            toast.success('Enrollment updated successfully.');
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update enrollment!');
        }
    };

    const customList = (title, items, search, onSearch, rankFilter, onRankChange, baseFilter, onBaseChange) => {
        const ranks = [...new Set(items.map(i => i.rank).filter(Boolean))].sort((a, b) => {
            const ap = crewRanksData.find(r => r.rankCode === a)?.rankPriority ?? 999;
            const bp = crewRanksData.find(r => r.rankCode === b)?.rankPriority ?? 999;
            if (ap !== bp) return ap - bp;
            return a.localeCompare(b);
        });

        const bases = [...new Set(items.map(i => i.base).filter(Boolean))].sort();

        const filtered = items.filter(item => {
            const q = search.toLowerCase();
            const matchSearch = !q ||
                item.employeeCode?.toLowerCase().includes(q) ||
                item.employeeName?.toLowerCase().includes(q) ||
                item.base?.toLowerCase().includes(q);
            const matchRank = !rankFilter || item.rank === rankFilter;
            const matchBase = !baseFilter || item.base === baseFilter;
            return matchSearch && matchRank && matchBase;
        });

        
        return (
            <Card sx={{ width: '100%' }}>
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    avatar={
                        <Checkbox
                            onClick={handleToggleAll(filtered)}
                            checked={numberOfChecked(filtered) === filtered.length && filtered.length !== 0}
                            indeterminate={
                                numberOfChecked(filtered) !== filtered.length &&
                                numberOfChecked(filtered) !== 0
                            }
                            disabled={filtered.length === 0}
                            slotProps={{ input: { 'aria-label': 'all items selected' } }}
                        />
                    }
                    title={title}
                    subheader={`${numberOfChecked(filtered)}/${filtered.length} selected`}
                />
                <Box px={1.5} pb={1} display="flex" gap={1}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={search}
                        onChange={onSearch}
                        sx={{ flex: 1 }}
                    />
                    <Select
                        size="small"
                        value={rankFilter}
                        onChange={onRankChange}
                        displayEmpty
                        sx={{ minWidth: 80 }}
                    >
                        <MenuItem value="">Rank</MenuItem>
                        {ranks.map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                    </Select>
                    <Select
                        size="small"
                        value={baseFilter}
                        onChange={onBaseChange}
                        displayEmpty
                        sx={{ minWidth: 80 }}
                    >
                        <MenuItem value="">Base</MenuItem>
                        {bases.map(b => (
                            <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Divider />
                <List
                    sx={{ width: '100%', height: 'clamp(320px, 55vh, 560px)', bgcolor: 'background.paper', overflow: 'auto' }}
                    dense
                    component="div"
                    role="list"
                >
                    {filtered.map((value) => {
                        const key = value.employeeCode || `${value.employeeName}-${value.rank}`;
                        return (
                            <ListItemButton
                                key={key}
                                role="listitem"
                                onClick={handleToggle(value)}
                                sx={{ py: 0.25 }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Checkbox
                                        checked={checked.includes(value)}
                                        tabIndex={-1}
                                        disableRipple
                                        size="small"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={`${value.employeeCode} - ${value.employeeName}`}
                                    secondary={`${value.rank || '-'} · ${value.base || '-'}`}
                                    slotProps={{
                                        primary: { variant: 'body2', noWrap: true },
                                        secondary: { variant: 'caption', noWrap: true },
                                    }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Card>
        );
    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Enrollment</DialogTitle>
            <DialogContent 
                sx={{ display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2, 
                }}
            >
                <Grid
                    container
                    spacing={2}
                    sx={{
                        flexDirection: isDesktop ? 'row' : 'column',
                        alignItems: isDesktop ? 'flex-start' : 'stretch',
                        flexWrap: 'nowrap',
                    }}
                >
                    <Grid sx={isDesktop ? { flex: 1, minWidth: 0 } : {}}>
                        {customList(
                            'Choices', left,
                            leftSearch, (e) => setLeftSearch(e.target.value),
                            leftRank, (e) => setLeftRank(e.target.value),
                            leftBase, (e) => setLeftBase(e.target.value),
                        )}
                    </Grid>

                    <Grid sx={{ flexShrink: 0 }}>
                        <Grid container direction={isDesktop ? 'column' : 'row'} gap={1} sx={{ alignItems: 'center' }}>
                            <StyledButton
                                aria-label="move selected right"
                                disabled={leftChecked.length === 0}
                                onClick={handleCheckedRight}
                                sx={leftChecked.length > 0 ? { border: 1, borderColor: 'primary.main' } : {}}
                            >
                                {isDesktop ? <ArrowForward /> : <ArrowDownward />}
                            </StyledButton>
                            <StyledButton
                                aria-label="move selected left"
                                disabled={rightChecked.length === 0}
                                onClick={handleCheckedLeft}
                                sx={rightChecked.length > 0 ? { border: 1, borderColor: 'primary.main' } : {}}
                            >
                                {isDesktop ? <ArrowBack /> : <ArrowUpward />}
                            </StyledButton>
                        </Grid>
                    </Grid>

                    <Grid sx={isDesktop ? { flex: 1, minWidth: 0 } : {}}>
                        {customList(
                            'Chosen', right,
                            rightSearch, (e) => setRightSearch(e.target.value),
                            rightRank, (e) => setRightRank(e.target.value),
                            rightBase, (e) => setRightBase(e.target.value),
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="outlined" onClick={handleSubmit}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnrollmentDialog;
